import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { useIsPending } from '#app/utils/misc.tsx'
import { ErrorList } from './forms.tsx'
import { Button } from './ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from './ui/dialog.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { RadioGroup, RadioGroupItem } from './ui/radio-group.tsx'
import { StatusButton } from './ui/status-button.tsx'
import { Textarea } from './ui/textarea.tsx'

const InquirySchema = z.object({
	intent: z.literal('submit-inquiry'),
	noteId: z.string(),
	noteTitle: z.string(),
	firstName: z.string().min(1, 'First name is required'),
	lastName: z.string().min(1, 'Last name is required'),
	email: z.string().email('Please enter a valid email'),
	phone: z.string().optional(),
	message: z.string().min(10, 'Please enter at least 10 characters'),
	hasRingnalda: z.enum(['yes', 'no']),
})

export function InquiryDialog({
	noteId,
	noteTitle,
	trigger,
	actionData,
}: {
	noteId: string
	noteTitle: string
	trigger?: React.ReactNode
	actionData?: any
}) {
	const { t } = useTranslation()
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'inquiry-form',
		constraint: getZodConstraint(InquirySchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: InquirySchema })
		},
		defaultValue: {
			hasRingnalda: 'no',
		},
	})

	return (
		<Dialog>
			<DialogTrigger asChild>
				{trigger || (
					<Button variant="default" size="lg" className="w-full sm:w-auto">
						{t('enquire')}
					</Button>
				)}
			</DialogTrigger>
			<DialogContent className="sm:max-w-[500px]">
				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<input type="hidden" name="intent" value="submit-inquiry" />
					<input type="hidden" name="noteId" value={noteId} />
					<input type="hidden" name="noteTitle" value={noteTitle} />

					<DialogHeader>
						<DialogTitle>{t('inquiryTitle')}</DialogTitle>
						<DialogDescription>{t('howCanWeReachYou')}</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						<div className="grid grid-cols-2 gap-4">
							<div>
								<Label htmlFor={fields.firstName.id}>
									{t('firstName')} <span className="text-destructive">*</span>
								</Label>
								<Input
									{...getInputProps(fields.firstName, { type: 'text' })}
									placeholder={t('firstName')}
								/>
								<div className="min-h-[20px] px-1 pt-1">
									<ErrorList
										id={fields.firstName.errorId}
										errors={fields.firstName.errors}
									/>
								</div>
							</div>
							<div>
								<Label htmlFor={fields.lastName.id}>
									{t('lastName')} <span className="text-destructive">*</span>
								</Label>
								<Input
									{...getInputProps(fields.lastName, { type: 'text' })}
									placeholder={t('lastName')}
								/>
								<div className="min-h-[20px] px-1 pt-1">
									<ErrorList
										id={fields.lastName.errorId}
										errors={fields.lastName.errors}
									/>
								</div>
							</div>
						</div>

						<div>
							<Label htmlFor={fields.email.id}>
								{t('email')} <span className="text-destructive">*</span>
							</Label>
							<Input
								{...getInputProps(fields.email, { type: 'email' })}
								placeholder="name@example.com"
							/>
							<div className="min-h-[20px] px-1 pt-1">
								<ErrorList
									id={fields.email.errorId}
									errors={fields.email.errors}
								/>
							</div>
						</div>

						<div>
							<Label htmlFor={fields.phone.id}>{t('phone')}</Label>
							<Input
								{...getInputProps(fields.phone, { type: 'tel' })}
								placeholder="+31 6 12345678"
							/>
							<div className="min-h-[20px] px-1 pt-1">
								<ErrorList
									id={fields.phone.errorId}
									errors={fields.phone.errors}
								/>
							</div>
						</div>

						<div>
							<Label htmlFor={fields.message.id}>
								{t('inquiryMessage')}{' '}
								<span className="text-destructive">*</span>
							</Label>
							<Textarea
								{...getTextareaProps(fields.message)}
								placeholder={t('inquiryMessage')}
								className="min-h-[100px] resize-y"
							/>
							<div className="min-h-[20px] px-1 pt-1">
								<ErrorList
									id={fields.message.errorId}
									errors={fields.message.errors}
								/>
							</div>
						</div>

						<div className="space-y-3">
							<Label>
								{t('hasPurchasedBefore')}{' '}
								<span className="text-destructive">*</span>
							</Label>
							<RadioGroup
								name={fields.hasRingnalda.name}
								defaultValue={fields.hasRingnalda.initialValue ?? 'no'}
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="yes" id="hasRingnalda-yes" />
									<Label
										htmlFor="hasRingnalda-yes"
										className="cursor-pointer font-normal"
									>
										{t('yes')}
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem value="no" id="hasRingnalda-no" />
									<Label
										htmlFor="hasRingnalda-no"
										className="cursor-pointer font-normal"
									>
										{t('no')}
									</Label>
								</div>
							</RadioGroup>
							<div className="min-h-[20px] px-1">
								<ErrorList
									id={fields.hasRingnalda.errorId}
									errors={fields.hasRingnalda.errors}
								/>
							</div>
						</div>
					</div>

					<DialogFooter className="flex-col gap-2 sm:flex-row">
						<StatusButton
							type="submit"
							disabled={isPending}
							status={isPending ? 'pending' : 'idle'}
							className="w-full sm:w-auto"
						>
							{t('submitInquiry')}
						</StatusButton>
						<Button
							type="button"
							variant="outline"
							className="w-full sm:w-auto"
						>
							{t('orGoBack')}
						</Button>
					</DialogFooter>

					<ErrorList id={form.errorId} errors={form.errors} />
				</Form>
			</DialogContent>
		</Dialog>
	)
}
